import React, { useMemo } from 'react'
import styled from 'styled-components'

import { safePtrAccess, usePRFile } from '@sketch-hq/sketch-web-renderer'
import { Rect } from '@sketch/inspector'
import { relativeMeasure } from '@sketch/utils'

const Outline = styled.div`
  position: absolute;
  pointer-events: none;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.sketch.A};
`

interface SelectedLayerOutlineProps {
  /** The layer picked from the changes list. */
  layerPathHash: string
  /** The scene root the position is taken as a percentage of. */
  rootExtent: Rect
}

/**
 * Outlines the changed layer picked from the list, so the camera arrives at
 * something marked. Only the selected one: a frame of forty outlined at once is
 * noise the list already covers.
 */
export function SelectedLayerOutline({
  layerPathHash,
  rootExtent,
}: SelectedLayerOutlineProps) {
  const prFile = usePRFile()

  const bounds = useMemo(() => {
    // The renderer may have deleted it since it was read; using it would throw
    const file = safePtrAccess(prFile)
    const node = file?.findByLayerPathHash(layerPathHash)

    // Its own box: getAbsoluteBounds() grows to whatever overflows it
    const rect = node?.getAbsoluteLayerBounds()

    return rect?.width && rect.height ? rect : null
  }, [layerPathHash, prFile])

  if (!bounds || !rootExtent.width || !rootExtent.height) return null

  return (
    <Outline
      data-testid="compare-selected-layer-outline"
      style={{
        left: `${relativeMeasure(bounds.x, rootExtent.width)}%`,
        top: `${relativeMeasure(bounds.y, rootExtent.height)}%`,
        width: `${relativeMeasure(bounds.width, rootExtent.width)}%`,
        height: `${relativeMeasure(bounds.height, rootExtent.height)}%`,
      }}
    />
  )
}
